/**
 * What the outing is carrying, across every car.
 *
 * Gear is declared per person, inside a car, so "do we have enough ropes?" meant
 * opening each card in turn and adding up. This folds the same declarations into
 * one place, read two ways: how many of each item are coming, and who is
 * bringing what — the second matters, because a rope travelling in a car nobody
 * has a seat in is not a rope anyone will climb on.
 *
 * Folded by default. Above the cars, expanded, it pushed the list of rides off
 * the screen — and the rides are what most people open the page for.
 */
export default function GearSummary({ rides }) {
  const byItem = new Map(); // item -> how many people bring it
  const byPerson = new Map(); // person -> the items they bring

  const add = (rawItem, who) => {
    const item = String(rawItem || '').trim();
    const name = String(who || '').trim();
    if (!item || !name) return;
    byItem.set(item, (byItem.get(item) ?? 0) + 1);
    if (!byPerson.has(name)) byPerson.set(name, []);
    if (!byPerson.get(name).includes(item)) byPerson.get(name).push(item);
  };

  for (const ride of rides) {
    if (ride.status === 'cancelled') continue;
    for (const g of ride.gear ?? []) add(g, ride.driver.name);
    for (const p of ride.passengers ?? []) for (const g of p.gear ?? []) add(g, p.name);
  }

  if (byItem.size === 0) return null;

  // Most-carried first; ties alphabetical, so the order never jitters between
  // refreshes for two items brought by the same number of people.
  const items = [...byItem.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'fr'),
  );
  const people = [...byPerson.entries()].sort((a, b) => a[0].localeCompare(b[0], 'fr'));
  const pieces = items.reduce((n, [, count]) => n + count, 0);

  return (
    <details className="gear-summary">
      <summary className="gear-head">
        <span className="section-label gear-title">🎒 Materiel de la sortie</span>
        <span className="small faint gear-tally" title={`${pieces} apports au total`}>
          {items.length} type{items.length > 1 ? 's' : ''} · {people.length} personne
          {people.length > 1 ? 's' : ''}
        </span>
        <span className="gear-chevron" aria-hidden="true">▾</span>
      </summary>

      <div className="gear-open">
        <ul className="gear-items">
          {items.map(([item, count]) => (
            <li key={item}>
              <span className="chip">
                {item}
                {count > 1 && <span className="gear-count">×{count}</span>}
              </span>
            </li>
          ))}
        </ul>

        <div className="section-label gear-subtitle">Qui apporte quoi</div>
        <ul className="gear-people">
          {people.map(([name, carried]) => (
            <li key={name}>
              <span className="gear-person truncate">{name}</span>
              <span className="gear-carried">
                {carried.map((item) => (
                  <span key={item} className="chip chip-sm">{item}</span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
}
